import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { FilenameConventionPayload } from 'src/app/models/filename-conventions/filename-convention-payload';
import {
  FilenameConventionWriteReason,
  FilenameConventionWriteReasonKind,
} from 'src/app/models/filename-conventions/filename-convention-write-reason';

export interface RangeFilenameConventionUpdateDialogData {
  organizationId: string;
  current: FilenameConvention;
}

type UpdateReasonKind = Exclude<FilenameConventionWriteReasonKind, 'initialCreate' | 'deleted'>;

const SELECTABLE_REASONS: readonly UpdateReasonKind[] = [
  'preferenceUpdate',
  'correction',
  'other',
];

const REASON_LABELS: Record<UpdateReasonKind, string> = {
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  other: 'Other',
};

@Component({
  selector: 'app-range-filename-convention-update-dialog',
  templateUrl: './range-filename-convention-update-dialog.component.html',
  styleUrls: ['./range-filename-convention-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RangeFilenameConventionUpdateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly familyBadge = 'Range';
  readonly selectableReasons = SELECTABLE_REASONS;
  readonly reasonLabel = (k: UpdateReasonKind): string => REASON_LABELS[k];

  readonly nameControl: FormControl<string>;
  readonly reasonControl = new FormControl<UpdateReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  submitting = false;
  rangePayload: FilenameConventionPayload;
  readonly rangeSeed: FilenameConventionPayload;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RangeFilenameConventionUpdateDialogData,
    private readonly dialogRef: MatDialogRef<
      RangeFilenameConventionUpdateDialogComponent,
      FilenameConvention | undefined
    >,
    private readonly service: FilenameConventionService,
  ) {
    this.nameControl = new FormControl<string>(data.current.name, {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    });
    this.rangePayload = data.current.value.payload;
    this.rangeSeed = data.current.value.payload;
  }

  get isOther(): boolean {
    return this.reasonControl.value === 'other';
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.reasonControl.value === null) return false;
    if (this.isOther && !this.detailControl.valid) return false;
    return true;
  }

  onRangeEditorSave(payload: FilenameConventionPayload): void {
    this.rangePayload = payload;
  }

  submit(): void {
    if (!this.canSubmit) return;
    const writeReason = this.buildWriteReason();
    if (writeReason === null) return;
    this.submitting = true;
    this.service
      .update(this.data.organizationId, this.data.current.id, {
        name: this.nameControl.value.trim(),
        value: { payload: this.rangePayload },
        writeReason,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FilenameConvention) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildWriteReason(): FilenameConventionWriteReason | null {
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') return { kind: 'other', detail: this.detailControl.value.trim() };
    return { kind };
  }
}
